import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { fetchCachedAuthUser, readLastVerifiedOfflineUser, } from "./utils/authCache";
import { clearAllAuthState, getUnifiedToken, } from "./utils/unifiedAuth";
import { persistPreferredLanguage, } from "./utils/avaaioLanguage";
import { usePreferences, } from "./context/PreferencesContext";
import { getAvaaioUiCopy, } from "./utils/avaaioUiCopy";
import System from "./pages/System";
import FunctionsPage from "./pages/education/FunctionsPage";
import FractionsPage from "./pages/education/FractionsPage";
import DivisionPage from "./pages/education/DivisionPage";
import MultiplicationPage from "./pages/education/MultiplicationPage";
import SubtractionPage from "./pages/education/SubtractionPage";
import AdditionPage from "./pages/education/AdditionPage";
import GradeLevelsPage from "./pages/education/GradeLevelsPage";
import FlashCardsPage from "./pages/education/FlashCardsPage";
import MathBoardPage from "./pages/education/MathBoardPage";
import TutorPage from "./pages/education/TutorPage";
import EduMath from "./pages/education/Math";
import EduHistory from "./pages/education/History";
import EduReading from "./pages/education/Reading";
import EduScience from "./pages/education/Science";
import Education from "./pages/Education";
import CheckersGame from "./pages/games/CheckersGame";
import ChessGame from "./pages/games/ChessGame";
import Chess3DBoard from "./pages/games/Chess3DBoard";
import GamesHub from "./pages/GamesHub";
import Entertainment from "./pages/Entertainment";
import Info from "./pages/Info";
import Support from "./pages/Support";
import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ReactivateAccount from "./pages/ReactivateAccount";
import SetupProfile from "./pages/SetupProfile";
import VerifyEmail from "./pages/VerifyEmail";
import Dashboard from "./pages/Dashboard";
import Setup from "./pages/Setup";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import AIDisclaimer from "./pages/AIDisclaimer";
import Credits from "./pages/Credits";
import Theme from "./pages/Theme";
import Profile from "./pages/Profile";
import Account from "./pages/Account";
import Settings from "./pages/Settings";
import Language from "./pages/Language";
import Subscribe from "./pages/Subscribe";
import SettingsPlanPanel from "./components/SettingsPlanPanel";
import ConnectionModePanel from "./components/ConnectionModePanel";
import PageBottomNav from "./components/PageBottomNav";
import AdminConsole from "./pages/AdminConsole";
const API_BASE = import.meta.env.VITE_API_BASE || "";
// __AVAAIO_SERVER_VERIFIED_SETUP_ROUTE_V9__
function ServerVerifiedSetupRoute() {
    const [routeState, setRouteState] = React.useState({
        checking: true,
        allowed: false,
        redirect: "/login",
    });
    React.useEffect(() => {
        let cancelled = false;
        async function verifySetupAuthorization() {
            const token = getUnifiedToken();
            try {
                const response = await fetch(`${API_BASE}/api/v1/auth/me`, {
                    method: "GET",
                    credentials: "include",
                    cache: "no-store",
                    headers: {
                        "Cache-Control": "no-cache",
                        Pragma: "no-cache",
                        ...(token
                            ? {
                                Authorization: `Bearer ${token}`,
                            }
                            : {}),
                    },
                });
                if (!response.ok) {
                    if (!cancelled) {
                        setRouteState({
                            checking: false,
                            allowed: false,
                            redirect: "/login",
                        });
                    }
                    return;
                }
                const payload = await response.json();
                const serverUser = payload?.user ||
                    payload?.profile ||
                    payload?.me ||
                    payload;
                const verificationValue = serverUser?.email_verified ??
                    serverUser?.is_verified;
                const serverVerified = verificationValue === true ||
                    verificationValue === 1 ||
                    verificationValue === "1";
                if (!serverVerified) {
                    try {
                        clearAllAuthState();
                    }
                    catch { }
                    if (!cancelled) {
                        setRouteState({
                            checking: false,
                            allowed: false,
                            redirect: "/login?verification=required",
                        });
                    }
                    return;
                }
                if (!cancelled) {
                    setRouteState({
                        checking: false,
                        allowed: true,
                        redirect: "",
                    });
                }
            }
            catch {
                if (!cancelled) {
                    setRouteState({
                        checking: false,
                        allowed: false,
                        redirect: "/login",
                    });
                }
            }
        }
        verifySetupAuthorization();
        return () => {
            cancelled = true;
        };
    }, []);
    if (routeState.checking) {
        return (_jsx("div", { className: "min-h-screen bg-[#02060d] text-white flex items-center justify-center", children: _jsx("div", { className: "text-cyan-300 text-sm tracking-[0.22em] uppercase", children: "Verifying secure account access\u2026" }) }));
    }
    if (!routeState.allowed) {
        return (_jsx(Navigate, { to: routeState.redirect, replace: true }));
    }
    return _jsx(SetupProfile, {});
}
function AdminAccessGate({ onVerified, }) {
    const [state, setState] = React.useState("checking");
    useEffect(() => {
        let disposed = false;
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => {
            controller.abort();
        }, 10000);
        const verifyAdmin = async () => {
            try {
                const token = getUnifiedToken();
                const response = await fetch(`${API_BASE}/api/v1/auth/me`, {
                    method: "GET",
                    credentials: "include",
                    cache: "no-store",
                    signal: controller.signal,
                    headers: {
                        Accept: "application/json",
                        ...(token
                            ? { Authorization: `Bearer ${token}` }
                            : {}),
                    },
                });
                if (disposed)
                    return;
                if (!response.ok) {
                    setState("denied");
                    return;
                }
                const payload = await response
                    .json()
                    .catch(() => null);
                const liveUser = payload?.user ?? payload;
                const role = String(liveUser?.role || "")
                    .trim()
                    .toLowerCase();
                const isAdmin = [
                    "admin",
                    "owner",
                    "superadmin",
                    "super_admin",
                ].includes(role);
                if (!isAdmin) {
                    setState("denied");
                    return;
                }
                const verifiedUser = {
                    role,
                    status: typeof liveUser?.status === "string"
                        ? liveUser.status
                        : undefined,
                    email: typeof liveUser?.email === "string"
                        ? liveUser.email
                        : undefined,
                    preferred_language: typeof liveUser?.preferred_language === "string"
                        ? liveUser.preferred_language
                        : undefined,
                    subscription_status: typeof liveUser?.subscription_status === "string"
                        ? liveUser.subscription_status
                        : undefined,
                };
                onVerified(verifiedUser);
                try {
                    localStorage.setItem("ava_user_role", role);
                    sessionStorage.setItem("ava_user_role", role);
                    localStorage.setItem("ava_chat_plan", "admin");
                    sessionStorage.setItem("ava_chat_plan", "admin");
                    window.dispatchEvent(new CustomEvent("ava-plan-updated", {
                        detail: {
                            role,
                            plan: "admin",
                            subscription_status: liveUser?.subscription_status,
                        },
                    }));
                }
                catch { }
                setState("allowed");
            }
            catch {
                if (!disposed) {
                    window.alert("Admin Console is unavailable while offline. " +
                        "Connect to the internet to manage AVAAIO. " +
                        "Your Local Mode session remains active.");
                    setState("offline");
                }
            }
            finally {
                window.clearTimeout(timeoutId);
            }
        };
        void verifyAdmin();
        return () => {
            disposed = true;
            window.clearTimeout(timeoutId);
            controller.abort();
        };
    }, [onVerified]);
    if (state === "checking") {
        return (_jsx("div", { className: "min-h-screen bg-[#02060d] text-white flex items-center justify-center", children: _jsx("div", { className: "text-cyan-300 text-sm tracking-[0.2em] uppercase", children: "Verifying Admin access\u2026" }) }));
    }
    if (state === "allowed") {
        return _jsx(AdminConsole, {});
    }
    if (state === "offline") {
        return _jsx(Navigate, { to: "/dashboard", replace: true });
    }
    return _jsx(Navigate, { to: "/login", replace: true });
}
export default function App() {
    const { preferences } = usePreferences();
    const interfaceCopy = getAvaaioUiCopy(preferences.language);
    const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
    const loggedOut = typeof window !== "undefined" &&
        new URLSearchParams(window.location.search).get("logged_out") === "1";
    const logoutLatch = typeof window !== "undefined" &&
        (() => {
            try {
                return sessionStorage.getItem("ava_logged_out") === "1";
            }
            catch {
                return false;
            }
        })();
    const logoutInProgress = loggedOut || logoutLatch;
    const isLoginRoute = pathname.startsWith("/login");
    const hasToken = typeof window !== "undefined" &&
        (() => {
            try {
                return !!(localStorage.getItem("token") ||
                    localStorage.getItem("access_token") ||
                    sessionStorage.getItem("token"));
            }
            catch {
                return false;
            }
        })();
    const shouldBypassBootstrap = isLoginRoute || (logoutInProgress && !hasToken);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(!shouldBypassBootstrap);
    const [languageReady, setLanguageReady] = useState(shouldBypassBootstrap);
    function resolveAuthToken() {
        return getUnifiedToken();
    }
    useEffect(() => {
        let cancelled = false;
        if (shouldBypassBootstrap) {
            setUser(null);
            setLanguageReady(true);
            setLoading(false);
            return () => {
                cancelled = true;
            };
        }
        setLanguageReady(false);
        setLoading(true);
        const offlineUser = typeof navigator !== "undefined" &&
            navigator.onLine === false
            ? readLastVerifiedOfflineUser()
            : null;
        if (offlineUser) {
            persistPreferredLanguage(offlineUser.preferred_language || "en");
            const offlineEntitlement = String(offlineUser.offline_entitlement ||
                "free")
                .trim()
                .toLowerCase();
            const offlineLocalPlan = offlineEntitlement === "admin" ||
                offlineEntitlement === "pro"
                ? "pro"
                : "free";
            try {
                // Local feature plan only. This does not grant Admin authority.
                localStorage.setItem("ava_chat_plan", offlineLocalPlan);
                sessionStorage.setItem("ava_chat_plan", offlineLocalPlan);
                localStorage.setItem("subscription_status", offlineLocalPlan === "pro"
                    ? "active"
                    : "free");
                sessionStorage.setItem("subscription_status", offlineLocalPlan === "pro"
                    ? "active"
                    : "free");
                // Administrative authority always remains restricted offline.
                localStorage.setItem("ava_user_role", "user");
                sessionStorage.setItem("ava_user_role", "user");
            }
            catch { }
            setUser({
                role: "user",
                status: offlineUser.status || "active",
                email: offlineUser.email,
                preferred_language: offlineUser.preferred_language,
                // Feature entitlement only.
                offline_entitlement: offlineUser.offline_entitlement,
                subscription_status: offlineUser.subscription_status,
            });
            setLanguageReady(true);
            setLoading(false);
            return () => {
                cancelled = true;
            };
        }
        const token = resolveAuthToken();
        fetchCachedAuthUser(`${API_BASE}/api/v1/auth/me`, {
            credentials: "include",
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        })
            .then((authUser) => {
            if (cancelled)
                return;
            if (authUser) {
                persistPreferredLanguage(authUser?.preferred_language || "en");
                setUser({
                    role: typeof authUser.role === "string" ? authUser.role : "user",
                    status: typeof authUser.status === "string" ? authUser.status : undefined,
                    email: typeof authUser.email === "string"
                        ? authUser.email
                        : undefined,
                    preferred_language: typeof authUser.preferred_language ===
                        "string"
                        ? authUser.preferred_language
                        : undefined,
                });
            }
            else {
                setUser(null);
            }
        })
            .catch((err) => {
            console.warn("[App] auth/me failed:", err);
            if (!cancelled)
                setUser(null);
        })
            .finally(() => {
            if (cancelled)
                return;
            window.setTimeout(() => {
                if (cancelled)
                    return;
                setLanguageReady(true);
                setLoading(false);
            }, 0);
        });
        return () => {
            cancelled = true;
        };
    }, [shouldBypassBootstrap]);
    // =====================================================
    // AVAAIO ONLINE ROLE RECOVERY
    //
    // Offline Local Mode intentionally restricts authority to
    // role:user. When internet access returns, verify server truth
    // immediately and restore the authenticated live account role.
    // =====================================================
    useEffect(() => {
        let disposed = false;
        let requestInFlight = false;
        const restoreLiveAccountRole = async () => {
            if (disposed ||
                requestInFlight ||
                typeof navigator === "undefined" ||
                navigator.onLine === false) {
                return;
            }
            requestInFlight = true;
            try {
                const token = resolveAuthToken();
                const response = await fetch(`${API_BASE}/api/v1/auth/me`, {
                    method: "GET",
                    credentials: "include",
                    cache: "no-store",
                    headers: {
                        Accept: "application/json",
                        ...(token
                            ? { Authorization: `Bearer ${token}` }
                            : {}),
                    },
                });
                if (!response.ok) {
                    // The existing heartbeat remains responsible for
                    // authoritative 401/403/404 revocation handling.
                    return;
                }
                const payload = await response
                    .json()
                    .catch(() => null);
                const liveUser = payload?.user ?? payload;
                if (disposed ||
                    !liveUser ||
                    typeof liveUser !== "object") {
                    return;
                }
                const liveRole = typeof liveUser.role === "string"
                    ? liveUser.role
                    : "user";
                setUser({
                    role: liveRole,
                    status: typeof liveUser.status === "string"
                        ? liveUser.status
                        : undefined,
                    email: typeof liveUser.email === "string"
                        ? liveUser.email
                        : undefined,
                    preferred_language: typeof liveUser.preferred_language ===
                        "string"
                        ? liveUser.preferred_language
                        : undefined,
                    subscription_status: typeof liveUser.subscription_status ===
                        "string"
                        ? liveUser.subscription_status
                        : undefined,
                });
                try {
                    localStorage.setItem("ava_user_role", liveRole);
                    sessionStorage.setItem("ava_user_role", liveRole);
                    const normalizedRole = String(liveRole)
                        .trim()
                        .toLowerCase();
                    const normalizedSubscription = String(liveUser.subscription_status || "")
                        .trim()
                        .toLowerCase();
                    const isAdmin = [
                        "admin",
                        "owner",
                        "superadmin",
                        "super_admin",
                    ].includes(normalizedRole);
                    const isPro = isAdmin ||
                        normalizedRole === "vip" ||
                        normalizedRole === "pro" ||
                        normalizedSubscription === "active";
                    const livePlan = isAdmin
                        ? "admin"
                        : isPro
                            ? "pro"
                            : "free";
                    localStorage.setItem("ava_chat_plan", livePlan);
                    sessionStorage.setItem("ava_chat_plan", livePlan);
                    localStorage.setItem("subscription_status", normalizedSubscription ||
                        (isPro ? "active" : "free"));
                    sessionStorage.setItem("subscription_status", normalizedSubscription ||
                        (isPro ? "active" : "free"));
                    window.dispatchEvent(new CustomEvent("ava-plan-updated", {
                        detail: {
                            role: liveRole,
                            plan: livePlan,
                            subscription_status: liveUser.subscription_status,
                        },
                    }));
                }
                catch { }
                console.log("[AVAAIO Online Recovery] Live role restored:", liveRole);
            }
            catch (error) {
                console.warn("[AVAAIO Online Recovery] Server still unreachable:", error);
            }
            finally {
                requestInFlight = false;
            }
        };
        const handleOnline = () => {
            void restoreLiveAccountRole();
        };
        window.addEventListener("online", handleOnline);
        if (navigator.onLine !== false) {
            void restoreLiveAccountRole();
        }
        return () => {
            disposed = true;
            window.removeEventListener("online", handleOnline);
        };
    }, [shouldBypassBootstrap]);
    // =====================================================
    // AVAAIO LIVE ACCOUNT ACCESS HEARTBEAT
    //
    // When an administrator bans, denies, or deletes a user,
    // /auth/me reads the current database record and returns
    // 401, 403, or 404. Clear the local session and return
    // the affected user to login on every open device.
    // =====================================================
    useEffect(() => {
        let disposed = false;
        let requestInFlight = false;
        const publicRoutePrefixes = [
            "/login",
            "/register",
            "/verify-email",
            "/forgot-password",
            "/reset-password",
            "/reactivate-account",
        ];
        const isPublicRoute = () => {
            const currentPath = typeof window !== "undefined"
                ? window.location.pathname
                : "/";
            return publicRoutePrefixes.some((prefix) => currentPath.startsWith(prefix));
        };
        const forceAccountLogout = async (status) => {
            if (disposed)
                return;
            disposed = true;
            try {
                sessionStorage.setItem("ava_logged_out", "1");
            }
            catch { }
            try {
                clearAllAuthState();
            }
            catch { }
            try {
                await fetch(`${API_BASE}/api/v1/auth/logout`, {
                    method: "POST",
                    credentials: "include",
                    cache: "no-store",
                });
            }
            catch {
                // The server may already have invalidated the account.
            }
            const reason = status === 403
                ? "access_revoked"
                : status === 404
                    ? "account_removed"
                    : "session_expired";
            window.location.replace(`/login?logged_out=1&reason=${reason}`);
        };
        const verifyLiveAccountAccess = async () => {
            if (disposed ||
                requestInFlight ||
                isPublicRoute() ||
                (typeof navigator !== "undefined" &&
                    navigator.onLine === false)) {
                return;
            }
            requestInFlight = true;
            try {
                const token = getUnifiedToken();
                const response = await fetch(`${API_BASE}/api/v1/auth/me`, {
                    method: "GET",
                    credentials: "include",
                    cache: "no-store",
                    headers: {
                        "Cache-Control": "no-cache",
                        Pragma: "no-cache",
                        ...(token
                            ? {
                                Authorization: `Bearer ${token}`,
                            }
                            : {}),
                    },
                });
                if (response.status === 401 ||
                    response.status === 403 ||
                    response.status === 404) {
                    await forceAccountLogout(response.status);
                }
            }
            catch {
                // Never log out merely because the network or API
                // is temporarily unavailable.
            }
            finally {
                requestInFlight = false;
            }
        };
        const handleFocus = () => {
            void verifyLiveAccountAccess();
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                void verifyLiveAccountAccess();
            }
        };
        void verifyLiveAccountAccess();
        const intervalId = window.setInterval(() => {
            void verifyLiveAccountAccess();
        }, 15000);
        window.addEventListener("focus", handleFocus);
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            disposed = true;
            window.clearInterval(intervalId);
            window.removeEventListener("focus", handleFocus);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);
    if (loading || !languageReady) {
        return (_jsx("div", { className: "min-h-screen bg-[#02060d] text-white flex items-center justify-center", children: _jsx("div", { className: "text-cyan-300 text-sm tracking-[0.25em] uppercase", children: interfaceCopy.dashboard.loadingAva }) }));
    }
    return (_jsxs(_Fragment, { children: [_jsxs(Routes, { children: [_jsx(Route, { path: "/", element: (typeof navigator !== "undefined" &&
                            navigator.onLine === false &&
                            readLastVerifiedOfflineUser())
                            ? _jsx(Navigate, { to: "/dashboard", replace: true })
                            : _jsx(Navigate, { to: "/login", replace: true }) }), _jsx(Route, { path: "/login/*", element: _jsx(Login, {}) }), _jsx(Route, { path: "/register", element: _jsx(Register, {}) }), _jsx(Route, { path: "/setup", element: _jsx(Setup, {}) }), _jsx(Route, { path: "/setup-profile", element: _jsx(ServerVerifiedSetupRoute, {}) }), _jsx(Route, { path: "/profile-setup", element: _jsx(ServerVerifiedSetupRoute, {}) }), _jsx(Route, { path: "/verify-email", element: _jsx(VerifyEmail, {}) }), _jsx(Route, { path: "/dashboard", element: user ? _jsx(Dashboard, {}) : _jsx(Navigate, { to: "/login", replace: true }) }), _jsx(Route, { path: "/privacy-policy", element: _jsx(PrivacyPolicy, {}) }), _jsx(Route, { path: "/terms-and-conditions", element: _jsx(TermsAndConditions, {}) }), _jsx(Route, { path: "/ai-disclaimer", element: _jsx(AIDisclaimer, {}) }), _jsx(Route, { path: "/credits", element: _jsx(Credits, {}) }), _jsx(Route, { path: "/admin", element: [
                            "admin",
                            "owner",
                            "superadmin",
                            "super_admin",
                        ].includes(String(user?.role || "")
                            .trim()
                            .toLowerCase()) ? (_jsx(AdminConsole, {})) : (_jsx(AdminAccessGate, { onVerified: setUser })) }), _jsx(Route, { path: "/info", element: _jsx(Info, {}) }), _jsx(Route, { path: "/support", element: _jsx(Support, {}) }), _jsx(Route, { path: "/entertainment", element: _jsx(Entertainment, {}) }), _jsx(Route, { path: "/games", element: _jsx(GamesHub, {}) }), _jsx(Route, { path: "/education", element: _jsx(Education, {}) }), _jsx(Route, { path: "/education/tutor", element: _jsx(TutorPage, {}) }), _jsx(Route, { path: "/education/decimals", element: _jsx(TutorPage, { initialTopic: "Decimals", lockTopic: true }) }), _jsx(Route, { path: "/education/essay-writing", element: _jsx(TutorPage, { initialTopic: "Essay Writing", lockTopic: true }) }), _jsx(Route, { path: "/education/mathboard", element: _jsx(MathBoardPage, {}) }), _jsx(Route, { path: "/education/math", element: _jsx(EduMath, {}) }), _jsx(Route, { path: "/education/history", element: _jsx(EduHistory, {}) }), _jsx(Route, { path: "/education/reading", element: _jsx(EduReading, {}) }), _jsx(Route, { path: "/education/science", element: _jsx(EduScience, {}) }), _jsx(Route, { path: "/education/flashcards", element: _jsx(FlashCardsPage, {}) }), _jsx(Route, { path: "/education/grades", element: _jsx(GradeLevelsPage, {}) }), _jsx(Route, { path: "/education/addition", element: _jsx(AdditionPage, {}) }), _jsx(Route, { path: "/education/subtraction", element: _jsx(SubtractionPage, {}) }), _jsx(Route, { path: "/education/multiplication", element: _jsx(MultiplicationPage, {}) }), _jsx(Route, { path: "/education/division", element: _jsx(DivisionPage, {}) }), _jsx(Route, { path: "/education/fractions", element: _jsx(FractionsPage, {}) }), _jsx(Route, { path: "/education/functions", element: _jsx(FunctionsPage, {}) }), _jsx(Route, { path: "/settings", element: _jsxs(_Fragment, { children: [_jsx(SettingsPlanPanel, {}), _jsx(ConnectionModePanel, {}), _jsx(Settings, {})] }) }), _jsx(Route, { path: "/language", element: _jsx(Language, {}) }), _jsx(Route, { path: "/system", element: _jsx(System, {}) }), _jsx(Route, { path: "/profile", element: _jsx(Profile, {}) }), _jsx(Route, { path: "/account", element: _jsx(Account, {}) }), _jsx(Route, { path: "/subscribe", element: _jsx(Subscribe, {}) }), _jsx(Route, { path: "/theme", element: _jsx(Theme, {}) }), _jsx(Route, { path: "/games/chess", element: _jsx(ChessGame, {}) }), _jsx(Route, { path: "/games/chess-3d-preview", element: _jsx(Chess3DBoard, {}) }), _jsx(Route, { path: "/games/checkers", element: _jsx(CheckersGame, {}) }), _jsx(Route, { path: "/forgot-password", element: _jsx(ForgotPassword, {}) }), _jsx(Route, { path: "/reset-password", element: _jsx(ResetPassword, {}) }), _jsx(Route, { path: "/reactivate", element: _jsx(ReactivateAccount, {}) }), _jsx(Route, { path: "*", element: _jsx(Navigate, { to: "/login", replace: true }) })] }), _jsx(PageBottomNav, { authenticated: Boolean(user) })] }));
}
